import { useState } from 'react'

import { type BoardElement, titleOf } from '../../api/boards'
import { Button } from '../../ui/Control'

/**
 * Finding something again on a board that has become full.
 *
 * One of the two ways back — the other is "fit everything in" — and deliberately **not** an overview
 * map. A map would be a second, smaller board with exactly the same problem on it, and it would cost
 * a great deal of drawing to arrive there.
 *
 * It filters what lies on *this* board and nothing else, by what each thing says about itself: a
 * card by the title of what it points at, a sticky note by its own words, a frame by its name. The
 * search over everything is ⌘K and is a different question — "where in MyLib is this?" rather than
 * "where on this surface did I put it?".
 *
 * Taking a hit is exactly the movement a deep link makes: the board jumps to it and holds it
 * highlighted. That is the whole reason this and `#/desk/<board>/<element>` are one piece of
 * behaviour rather than two.
 */
export interface FindOnBoardProps {
  readonly elements: readonly BoardElement[]
  readonly onGo: (element: BoardElement) => void
  readonly onClose: () => void
}

export function FindOnBoard({ elements, onGo, onClose }: FindOnBoardProps) {
  const [line, setLine] = useState('')
  const found = matching(elements, line)

  return (
    <div className="board__find" role="search">
      <div className="board__findHead">
        <input
          className="board__findField"
          type="search"
          // Focused on appearing: a control was just used to ask for exactly this field.
          autoFocus
          value={line}
          aria-label="Find on this board"
          placeholder="Find on this board"
          onChange={(event) => {
            setLine(event.target.value)
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              onClose()
            }
            if (event.key === 'Enter' && found[0] !== undefined) {
              onGo(found[0])
            }
          }}
        />
        <Button tone="bare" icon="close" iconOnlyLabel="Close the search" onClick={onClose} />
      </div>

      {line.trim() !== '' && (
        <ul className="board__found">
          {found.length === 0 ? (
            <li className="board__nothing">Nothing on this board says that.</li>
          ) : (
            found.map((element) => (
              <li key={element.id}>
                <button
                  type="button"
                  className="board__hit"
                  onClick={() => {
                    onGo(element)
                  }}
                >
                  <span className="board__hitWhat">{kindOf(element)}</span>
                  <span className="board__hitTitle">{titleOf(element) || 'Untitled'}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  )
}

/** What each thing on the board says about itself, matched loosely and case-insensitively. */
function matching(elements: readonly BoardElement[], line: string): BoardElement[] {
  const asked = line.trim().toLowerCase()
  if (asked === '') {
    return []
  }
  return elements
    .filter((element) => saysAbout(element).toLowerCase().includes(asked))
    .slice(0, MOST_SHOWN)
}

function saysAbout(element: BoardElement): string {
  return [titleOf(element), element.text ?? '', element.label ?? '', element.note?.text ?? ''].join(
    ' ',
  )
}

function kindOf(element: BoardElement): string {
  switch (element.kind) {
    case 'card':
      return element.document !== null
        ? 'Document'
        : element.note !== null
          ? 'Note'
          : element.task !== null
            ? 'Task'
            : 'Card'
    case 'sticky':
      return 'Sticky note'
    case 'text':
      return 'Text'
    default:
      return 'Frame'
  }
}

/** Enough to choose from, few enough that the list does not become the board. */
const MOST_SHOWN = 12
